import React, { useState } from "react";
import { toast } from "react-toastify";
import Select from "react-select";
import { IoClose, IoCheckmark } from "react-icons/io5";
import { updateBOQ } from "../../../../../services/budgetService";
import {
  materialUnitOptions,
  supplyApplyOptions,
  boqTypeOptions,
  normalizeSupplyApply,
  requiresLeadTime,
} from "../../../../../constants/boq";

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 44,
    height: 44,
    borderRadius: 12,
    borderColor: "#d1d5db",
    fontSize: 12,
    boxShadow: "none",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: 44 }),
  menu: (base) => ({ ...base, fontSize: 12 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const inputStyle = {
  height: 44,
  width: "100%",
  borderRadius: 12,
  border: "1px solid #d1d5db",
  padding: "8px 12px 0",
  fontSize: 14,
  color: "#0D0D0E",
  boxSizing: "border-box",
};
const floatLabelStyle = {
  position: "absolute",
  left: 12,
  top: -8,
  fontSize: 12,
  color: "#6b7280",
  background: "#fff",
  padding: "0 4px",
};

export const InlineEditBoqRow = ({
  boq,
  inclusion,
  projectId,
  onSave,
  onCancel,
  isSaving,
  showCostFields = true,
  showUnitCostInEdit = true,
  onBudgetUpdated,
  isSiteVisit = false,
  isSiteEstimatesRoute = false,
  isUncovered = false,
}) => {
  const existingUnitCostRaw =
    boq.type === "Activities" ? boq.manpower_rate : boq.unit_cost;
  const [unitCost, setUnitCost] = useState(
    existingUnitCostRaw != null ? String(existingUnitCostRaw) : "",
  );

  const [item_name, setItemName] = useState(boq.item_name || "");
  const [boq_type, setBoqType] = useState(boq.type || "Materials");
  const [subcategory] = useState(boq.subcategory || "");
  // Normalize incoming supply_type so legacy lowercase ("supply"/"apply")
  // matches the capitalized options the dropdown renders.
  const [supply_apply, setSupplyApply] = useState(
    normalizeSupplyApply(boq.supply_type) || null,
  );
  const [specification, setSpecification] = useState(boq.specification || "");
  const [material_unit, setMaterialUnit] = useState(boq.unit_of_measurement || null);
  const [quantity, setQuantity] = useState(boq.quantity != null ? String(boq.quantity) : "");
  const [days, setDays] = useState(boq.days != null ? String(boq.days) : "");
  const [leadTime, setLeadTime] = useState(
    boq.lead_time != null ? String(boq.lead_time) : "",
  );
  const showLeadTime = requiresLeadTime(supply_apply);

  // Image upload/delete moved to the read-only BOQItem/BOQItemCard
  // (mirrors reno-portal). The inline edit row no longer renders an
  // image cell or talks to the file APIs.
  const [submitting, setSubmitting] = useState(false);

  const selectedSupplyApplyOption =
    supplyApplyOptions.find(
      (o) =>
        o.value === supply_apply ||
        String(o.value).toLowerCase() === String(supply_apply || "").toLowerCase(),
    ) || null;

  const handleNumberChange = (value, isMaterials) => {
    const cleaned = value === "" ? "" : value.replace(/[^0-9]/g, "");
    if (cleaned === "" || (!isNaN(cleaned) && parseInt(cleaned) >= 0)) {
      if (isMaterials) {
        setQuantity(cleaned);
        setDays("");
      } else {
        setDays(cleaned);
        setQuantity("");
      }
    }
  };

  const handleSubmit = async () => {
    if (boq_type === "Materials" && (quantity == null || quantity === "")) {
      toast.error("Please enter quantity for materials");
      return;
    }
    if (boq_type === "Activities" && (days == null || days === "")) {
      toast.error("Please enter number of days for activity");
      return;
    }
    if (boq_type === "Materials" && !material_unit) {
      toast.error("Material Unit is required");
      return;
    }

    // Lead Time is MANDATORY in reno-web (contractor) whenever supply
    // is "Supply" or "Supply and Apply" — positive integer required.
    if (showLeadTime) {
      const trimmedLT = String(leadTime ?? "").trim();
      if (trimmedLT === "") {
        toast.error("Please enter Lead Time (days)");
        return;
      }
      if (!/^\d+$/.test(trimmedLT)) {
        toast.error("Lead Time must be a positive integer");
        return;
      }
    }

    const showUnitCostRow = showUnitCostInEdit && showCostFields && !isSiteVisit;
    if (showUnitCostRow) {
      const trimmed = String(unitCost ?? "").trim();
      if (isSiteEstimatesRoute) {
        if (trimmed === "") {
          toast.error("Please enter unit cost to proceed");
          return;
        }
        if (Number(trimmed) === 0) {
          toast.error("Enter a valid cost.");
          return;
        }
      } else if (trimmed !== "" && Number(trimmed) === 0) {
        toast.error("Enter a valid cost.");
        return;
      }
    }

    if (!projectId) {
      toast.error("Cannot save: project not found");
      return;
    }

    const supplyType = selectedSupplyApplyOption?.value ?? supply_apply ?? "";

    const unitCostFallback =
      boq_type === "Activities" ? boq.manpower_rate : boq.unit_cost;
    const unitCostValue = (() => {
      if (!showUnitCostRow) return unitCostFallback ?? null;
      const trimmed = String(unitCost ?? "").trim();
      if (trimmed !== "") return trimmed;
      return unitCostFallback ?? null;
    })();

    const payload = {
      name: item_name,
      boq_type,
      subcategory: subcategory ?? "",
      supply_type: supplyType,
      specification,
      lead_time:
        showLeadTime && leadTime != null && String(leadTime).trim() !== ""
          ? parseInt(String(leadTime).trim(), 10)
          : null,
      ...(boq_type === "Activities"
        ? {
            days,
            manpower_rate: unitCostValue,
            qty: null,
            material_unit_price: null,
          }
        : {
            material_unit,
            qty: quantity,
            material_unit_price: unitCostValue,
            days: null,
            manpower_rate: null,
          }),
    };

    try {
      setSubmitting(true);
      const data = await updateBOQ(projectId, boq.id, payload);
      if (data?.success !== false) {
        toast.success("BOQ updated");
        onBudgetUpdated?.();
        onSave?.();
      } else {
        toast.error("Failed to update BOQ");
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to update BOQ");
    } finally {
      setSubmitting(false);
    }
  };

  const isMaterials = boq_type === "Materials";
  const showUnitCostRow = showUnitCostInEdit && showCostFields && !isSiteVisit;
  const busy = submitting || isSaving;

  return (
    <div
      style={{
        position: "relative",
        zIndex: 10,
        display: "flex",
        gap: 16,
        backgroundColor: "#fff",
        border: `2px solid ${isUncovered ? "#f87171" : "#93c5fd"}`,
        borderRadius: 12,
        padding: 16,
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", flex: 1, gap: 12, minWidth: 0 }}>
        {/* Row 1 */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
          <div style={{ position: "relative", flex: 1, minWidth: 140 }}>
            <input style={inputStyle} value={item_name} onChange={(e) => setItemName(e.target.value)} />
            <label style={floatLabelStyle}>Item Name</label>
          </div>
          <div style={{ position: "relative", flex: 1, minWidth: 140 }}>
            <Select
              value={selectedSupplyApplyOption}
              onChange={(selected) => {
                const v = selected?.value || null;
                setSupplyApply(v);
                if (!requiresLeadTime(v)) setLeadTime("");
              }}
              options={supplyApplyOptions}
              styles={selectStyles}
              menuPosition="fixed"
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            />
            <label style={floatLabelStyle}>Supply/Apply</label>
          </div>
          {showLeadTime && (
            <div style={{ position: "relative", width: 140, minWidth: 120 }}>
              <input
                type="text"
                inputMode="numeric"
                style={inputStyle}
                placeholder="Days"
                value={leadTime}
                onChange={(e) =>
                  setLeadTime(e.target.value.replace(/[^0-9]/g, ""))
                }
              />
              <label style={floatLabelStyle}>Lead Time (days)</label>
            </div>
          )}
          <div style={{ position: "relative", width: 120, minWidth: 100 }}>
            <input
              style={inputStyle}
              placeholder={isMaterials ? "Qty" : "Days"}
              value={isMaterials ? quantity : days}
              onChange={(e) => handleNumberChange(e.target.value, isMaterials)}
              type="text"
              inputMode="numeric"
            />
            <label style={floatLabelStyle}>{isMaterials ? "Quantity" : "Days"}</label>
          </div>
          {isMaterials && (
            <div style={{ position: "relative", width: 170, minWidth: 140 }}>
              <Select
                value={materialUnitOptions.find((o) => o.value === material_unit) || null}
                onChange={(selected) => setMaterialUnit(selected?.value || null)}
                options={materialUnitOptions}
                styles={selectStyles}
                menuPosition="fixed"
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              />
              <label style={floatLabelStyle}>Material Unit</label>
            </div>
          )}
          {showUnitCostRow && (
            <div style={{ position: "relative", width: 150, minWidth: 120 }}>
              <input
                type="text"
                inputMode="numeric"
                value={unitCost}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "" || /^\d+$/.test(v)) setUnitCost(v);
                }}
                placeholder="Enter Unit Cost"
                style={inputStyle}
              />
              <label style={floatLabelStyle}>Unit Cost</label>
            </div>
          )}
        </div>

        {/* Row 2 */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
          <div style={{ position: "relative", flex: 1, minWidth: 140 }}>
            <Select
              value={boqTypeOptions.find((o) => o.value === boq_type) || boqTypeOptions[0]}
              onChange={(selected) => {
                const newType = selected?.value || "Materials";
                setBoqType(newType);
                if (newType === "Materials") setDays("");
                else {
                  setQuantity("");
                  setMaterialUnit(null);
                }
              }}
              options={boqTypeOptions}
              styles={selectStyles}
              menuPosition="fixed"
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            />
            <label style={floatLabelStyle}>Material Type</label>
          </div>
          <div style={{ position: "relative", flex: 2, minWidth: 200 }}>
            <input
              style={inputStyle}
              placeholder="Enter specification"
              value={specification}
              onChange={(e) => setSpecification(e.target.value)}
            />
            <label style={floatLabelStyle}>Specifications</label>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, alignItems: "center", flexShrink: 0 }}>
        <button
          onClick={handleSubmit}
          disabled={busy}
          style={{
            padding: 8,
            color: "#16a34a",
            background: "transparent",
            border: "none",
            borderRadius: 8,
            cursor: busy ? "not-allowed" : "pointer",
            opacity: busy ? 0.5 : 1,
          }}
          title="Save"
        >
          <IoCheckmark style={{ width: 20, height: 20 }} />
        </button>
        <button
          onClick={onCancel}
          disabled={busy}
          style={{
            padding: 8,
            color: "#ef4444",
            background: "transparent",
            border: "none",
            borderRadius: 8,
            cursor: busy ? "not-allowed" : "pointer",
            opacity: busy ? 0.5 : 1,
          }}
          title="Cancel"
        >
          <IoClose style={{ width: 20, height: 20 }} />
        </button>
      </div>
    </div>
  );
};

export default InlineEditBoqRow;
